import type { CSSProperties, FC } from 'react';
import type { IconType } from 'react-icons';
import {
    TbBuilding,
    TbBuildingBank,
    TbBuildingFactory2,
    TbChartBar,
    TbChartPie,
    TbCoins,
    TbCrosshair,
    TbDatabase,
    TbDeviceDesktop,
    TbFileDescription,
    TbLeaf,
    TbScale,
    TbShieldCheck,
    TbTruck,
    TbUsers,
    TbUsersGroup,
    TbWorld,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import SmallTag from '@/shared/components/tags/SmallTag';
import type { ResearchDomainDefinition, ResearchDomainId } from '@/shared/document-intelligence/researchOntology';

import { getDocumentConceptPalette } from './documentConceptPalette';
import type { DocumentConcept, DocumentConceptReference } from './documentIntelligence';

const RESEARCH_DOMAIN_ICONS: Readonly<Record<ResearchDomainId, IconType>> = {
    'capital-structure-liquidity-valuation': TbCoins,
    'commercial-customers-pricing': TbUsers,
    'financial-reporting-performance': TbChartBar,
    'governance-ownership-leadership': TbBuildingBank,
    'human-capital-organization': TbUsersGroup,
    'legal-regulatory-compliance': TbScale,
    'macroeconomics-rates-policy': TbWorld,
    'market-industry-competition': TbBuilding,
    'operating-metrics-unit-economics': TbBuildingFactory2,
    'operations-assets-supply-chain': TbTruck,
    'portfolio-investment-analytics': TbChartPie,
    'products-technology-intellectual-property': TbDeviceDesktop,
    'risk-controls-resilience': TbShieldCheck,
    'strategy-growth-outlook': TbCrosshair,
    'sustainability-climate-natural-resources': TbLeaf,
    'transactions-contracts-commitments': TbFileDescription,
};

interface DocumentConceptIconProps {
    color?: CSSProperties['color'];
    domainId?: ResearchDomainId;
    size?: string;
}

export const DocumentConceptIcon: FC<DocumentConceptIconProps> = ({ color, domainId, size = '0.875rem' }) => {
    const Icon = domainId ? RESEARCH_DOMAIN_ICONS[domainId] : TbDatabase;

    return <Icon aria-hidden='true' size={size} style={{ color, display: 'block', flex: 'none' }} />;
};

interface DocumentConceptTagProps {
    active?: boolean;
    concept: DocumentConcept;
    domain?: ResearchDomainDefinition;
    reference: DocumentConceptReference;
    text: string;
    onSelect: (reference: DocumentConceptReference) => void;
}

/**
 * Shared click-through presentation for an ontology-backed document term.
 * It deliberately mirrors financial formula operands while retaining the
 * reference identity required for bidirectional document navigation.
 */
const DocumentConceptTag: FC<DocumentConceptTagProps> = ({
    active = false,
    concept,
    domain,
    onSelect,
    reference,
    text,
}) => {
    const { token } = useAntDesignTheme();
    const palette = getDocumentConceptPalette(token, concept, domain?.order);
    const domainLabel = domain?.label ?? 'Document-Specific';

    return (
        <button
            aria-current={active ? 'location' : undefined}
            aria-label={`Show ${concept.label} in ${domainLabel}`}
            data-ontology-concept-id={concept.id}
            data-ontology-domain-id={domain?.id ?? 'document-specific'}
            data-ontology-reference-id={reference.id}
            title={`${concept.label} · ${domainLabel}`}
            type='button'
            onClick={() => onSelect(reference)}
            style={{
                background: 'transparent',
                border: 0,
                borderRadius: token.borderRadius,
                color: 'inherit',
                cursor: 'pointer',
                display: 'inline-flex',
                font: 'inherit',
                lineHeight: 'inherit',
                margin: '0 0.125rem',
                padding: 0,
                verticalAlign: '-0.175rem',
            }}
        >
            <SmallTag
                bordered
                icon={<DocumentConceptIcon color={palette.text} domainId={concept.primaryDomainId} />}
                message={text}
                padding='0.125rem 0.375rem'
                styles={{
                    alignItems: 'center',
                    backgroundColor: active ? palette.activeBackground : palette.background,
                    border: `1px dashed ${palette.border}`,
                    boxShadow: active ? `0 0 0 2px ${token.colorBgContainer}, 0 0 0 4px ${palette.border}` : undefined,
                    color: palette.text,
                    display: 'inline-flex',
                    fontWeight: token.fontWeightStrong,
                    gap: '0.25rem',
                    lineHeight: 1.2,
                    marginLeft: 0,
                    whiteSpace: 'nowrap',
                }}
            />
        </button>
    );
};

export default DocumentConceptTag;
